import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, PanelTop, HardHat, Gauge, Activity, Droplets, Zap, BatteryCharging, CircuitBoard, PlugZap, Lightbulb, Bug } from "lucide-react";
import { SectionHead } from "@/components/site/SectionHead";
import { SERVICES } from "@/content/site";

const ICONS = { PanelTop, HardHat, Gauge, Activity, Droplets, Zap, BatteryCharging, CircuitBoard, PlugZap, Lightbulb, Bug };
const SPAN = { installation: "md:col-span-2 lg:row-span-2", electrical: "md:col-span-2", batteries: "md:col-span-2" };

const ServiceCard = ({ s, i }) => {
  const Icon = ICONS[s.icon];
  const big = s.id === "installation";
  return (
    <motion.article
      data-testid={`service-card-${s.id}`}
      initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.1 }}
      transition={{ duration: 0.9, delay: (i % 4) * 0.07, ease: [0.16, 1, 0.3, 1] }}
      className={`card-lux group relative flex flex-col overflow-hidden rounded-2xl p-7 sm:p-8 ${SPAN[s.id] || ""} ${big ? "min-h-[340px] lg:min-h-0" : "min-h-[260px]"}`}
    >
      {big && (
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute -bottom-24 -right-24 h-80 w-80 rounded-full bg-amber-500/20 blur-[80px] sun-breathe" />
          <div className="absolute inset-0 grid-lines opacity-50 [mask-image:linear-gradient(to_top,black,transparent)]" />
        </div>
      )}
      <div className="relative flex items-center justify-between">
        <div className="grid h-12 w-12 place-items-center rounded-xl border border-white/10 bg-white/[0.03] transition-[border-color,background-color] duration-500 group-hover:border-amber-400/50 group-hover:bg-amber-400/10">
          <Icon className="h-5 w-5 text-amber-400" />
        </div>
        <span className="font-mono text-xs text-zinc-500">S/{String(i + 1).padStart(2, "0")}</span>
      </div>
      <div className="relative mt-auto pt-10">
        <h3 className={`${big ? "text-2xl sm:text-3xl" : "text-lg"} font-semibold uppercase tracking-tight text-white`}>{s.title}</h3>
        <p className={`mt-3 ${big ? "max-w-md text-base" : "text-sm"} leading-relaxed text-zinc-300`}>{s.text}</p>
        <Link to={s.href} data-testid={`service-learn-more-${s.id}`} className="mt-6 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-amber-400">
          Learn More
          <ArrowUpRight className="h-4 w-4 transition-transform duration-500 group-hover:translate-x-1 group-hover:-translate-y-1" />
        </Link>
      </div>
    </motion.article>
  );
};

export const Services = () => (
  <section id="services" data-testid="services-section" className="relative bg-[#0b0c0e] py-24 sm:py-32">
    <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12">
      <SectionHead index="02" eyebrow="Services" lines={["Complete solar &", "energy services"]}
        sub="From installation to troubleshooting, Solar Energy Doc keeps your energy system performing at its best." />
      <div className="mt-16 grid auto-rows-auto gap-4 md:grid-cols-2 lg:grid-cols-4">
        {SERVICES.map((s, i) => <ServiceCard key={s.id} s={s} i={i} />)}
      </div>
    </div>
  </section>
);
